import { RADIO_QUEUE_KINDS } from './hitLabRadioTracks';

export const RADIO_LIVE_CAPSULE_READY_TEXT =
  'Radio Rotation is getting ready.';

const cleanText = (value, max = 256) =>
  String(value || '').trim().slice(0, max);

const normalizeIndex = (index, length) => {
  if (!length) return 0;
  const next = Number(index);
  if (!Number.isFinite(next)) return 0;
  return ((Math.trunc(next) % length) + length) % length;
};

const getTrackTitle = (item) =>
  cleanText(item?.songTitle || item?.title, 160);

const getTrackArtistName = (item) => cleanText(item?.artistName, 120);

const toIsoString = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toISOString();
};

const normalizeSignalTrack = (item = {}, dateField) => {
  const trackId = cleanText(item?.trackId || item?.id, 120);
  const title = cleanText(item?.title || item?.songTitle, 160);
  const artistName = cleanText(item?.artistName, 120);
  const timestamp = toIsoString(item?.[dateField]);
  if (!trackId || !title || !artistName || !timestamp) return null;
  return {
    trackId,
    title,
    artistName,
    [dateField]: timestamp,
    isFreshRotationSignalEligible:
      item?.isFreshRotationSignalEligible === true,
    newRotationMessage: cleanText(item?.newRotationMessage, 220),
  };
};

const normalizeRecentMovementTrack = (item = {}) => {
  const trackId = cleanText(item?.trackId || item?.id, 120);
  const title = cleanText(item?.title || item?.songTitle, 160);
  const artistName = cleanText(item?.artistName, 120);
  const lastPlayedAt = toIsoString(item?.lastPlayedAt);
  const playCount = Number(item?.playCount || 0);
  if (!trackId || !title || !artistName || !lastPlayedAt) return null;
  return {
    trackId,
    title,
    artistName,
    coverArtUrl: cleanText(item?.coverArtUrl, 2048),
    lastPlayedAt,
    playCount:
      Number.isFinite(playCount) && playCount > 0 ? Math.floor(playCount) : 0,
  };
};

const normalizeNewRotationSignalTrack = (item = {}) => {
  const trackId = cleanText(item?.trackId || item?.id, 120);
  const title = cleanText(item?.title || item?.songTitle, 160);
  const artistName = cleanText(item?.artistName, 120);
  const newInRotationUntil = toIsoString(item?.newInRotationUntil);
  if (!trackId || !title || !artistName || !newInRotationUntil) return null;
  return {
    trackId,
    announcement: item.announcement || null,
    title,
    artistName,
    newInRotationUntil,
    isFreshRotationSignalEligible:
      item?.isFreshRotationSignalEligible === true,
    newRotationMessage: cleanText(item?.newRotationMessage, 220),
  };
};

const isFreshSignal = (signal = {}, dateField, nowMs, ttlMs) => {
  const timestamp = new Date(signal?.[dateField] || '').getTime();
  if (!Number.isFinite(timestamp)) return false;
  return timestamp <= nowMs && nowMs - timestamp <= ttlMs;
};

const normalizeCtaUrl = (value) => {
  const raw = cleanText(value, 240);
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '';
  if (/[\u0000-\u001f\u007f\\]/.test(raw)) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return '';
  return raw;
};

const normalizeProgrammingMessage = (item = null) => {
  if (!item || typeof item !== 'object') return null;
  const message = cleanText(item.message, 120);
  if (!message) return null;
  return {
    eyebrow: cleanText(item.eyebrow, 32),
    message,
    ctaLabel: cleanText(item.ctaLabel, 32),
    ctaUrl: normalizeCtaUrl(item.ctaUrl),
    activeUntil: toIsoString(item.activeUntil),
  };
};

const normalizePositiveInteger = (value) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.floor(parsed);
};

const cleanProofDisplay = (value, label) => {
  const text = cleanText(value, 80);
  return text.toLowerCase().includes(label) ? text : '';
};

const buildProofDisplay = (count, label) =>
  count > 0 ? `${count.toLocaleString()} ${label}` : '';

const normalizeProofSummary = (item = null) => {
  if (!item || typeof item !== 'object') {
    return {
      hasProofActivity: false,
      primaryDisplay: '',
    };
  }

  const qualifiedPlaySignalCount = normalizePositiveInteger(
    item.qualifiedPlaySignalCount,
  );
  const feedbackResponseCount = normalizePositiveInteger(
    item.feedbackResponseCount,
  );
  const artistSupportActionCount = normalizePositiveInteger(
    item.artistSupportActionCount,
  );

  const qualifiedPlaySignalDisplay =
    cleanProofDisplay(
      item.qualifiedPlaySignalDisplay,
      'qualified plays',
    ) || buildProofDisplay(qualifiedPlaySignalCount, 'qualified plays');
  const feedbackResponseDisplay =
    cleanProofDisplay(item.feedbackResponseDisplay, 'feedback responses') ||
    buildProofDisplay(feedbackResponseCount, 'feedback responses');
  const artistSupportActionDisplay =
    cleanProofDisplay(
      item.artistSupportActionDisplay,
      'artist support actions',
    ) || buildProofDisplay(artistSupportActionCount, 'artist support actions');

  const primaryDisplay =
    cleanProofDisplay(item.primaryDisplay, 'qualified plays') ||
    qualifiedPlaySignalDisplay ||
    feedbackResponseDisplay ||
    artistSupportActionDisplay ||
    '';
  const hasProofActivity = Boolean(
    item.hasProofActivity === true &&
      (primaryDisplay ||
        qualifiedPlaySignalDisplay ||
        feedbackResponseDisplay ||
        artistSupportActionDisplay),
  );

  return {
    hasProofActivity,
    primaryDisplay: hasProofActivity ? primaryDisplay : '',
    qualifiedPlaySignalCount: qualifiedPlaySignalCount || null,
    qualifiedPlaySignalDisplay: qualifiedPlaySignalDisplay || null,
    feedbackResponseCount: feedbackResponseCount || null,
    feedbackResponseDisplay: feedbackResponseDisplay || null,
    artistSupportActionCount: artistSupportActionCount || null,
    artistSupportActionDisplay: artistSupportActionDisplay || null,
  };
};

export const getRadioProofSummaryFingerprint = (proofSummary = null) => {
  const safeSummary = normalizeProofSummary(proofSummary);
  if (!safeSummary.hasProofActivity || !safeSummary.primaryDisplay) return '';
  return [
    safeSummary.primaryDisplay,
    safeSummary.qualifiedPlaySignalCount || 0,
    safeSummary.qualifiedPlaySignalDisplay || '',
    safeSummary.feedbackResponseCount || 0,
    safeSummary.feedbackResponseDisplay || '',
    safeSummary.artistSupportActionCount || 0,
    safeSummary.artistSupportActionDisplay || '',
  ].join('|');
};

const normalizeRecentMovement = (item = null) => {
  const activeWindowMinutes = Number(item?.activeWindowMinutes || 15);
  const recentWindowMinutes = Number(item?.recentWindowMinutes || 120);
  const lastMovementTrackId = cleanText(
    item?.lastMovement?.trackId || item?.lastMovement?.id,
    120,
  );
  const lastMovementTitle = cleanText(
    item?.lastMovement?.title || item?.lastMovement?.songTitle,
    160,
  );
  const lastMovementArtistName = cleanText(item?.lastMovement?.artistName, 120);
  const lastMovementAt = toIsoString(item?.lastMovement?.at);
  const lastMovement =
    lastMovementTrackId && lastMovementTitle && lastMovementArtistName && lastMovementAt
      ? {
          type: cleanText(item?.lastMovement?.type || 'play', 40),
          trackId: lastMovementTrackId,
          title: lastMovementTitle,
          artistName: lastMovementArtistName,
          at: lastMovementAt,
        }
      : null;

  return {
    generatedAt: toIsoString(item?.generatedAt),
    activeWindowMinutes:
      Number.isFinite(activeWindowMinutes) && activeWindowMinutes > 0
        ? Math.floor(activeWindowMinutes)
        : 15,
    recentWindowMinutes:
      Number.isFinite(recentWindowMinutes) && recentWindowMinutes > 0
        ? Math.floor(recentWindowMinutes)
        : 120,
    closeRecentPlays: (Array.isArray(item?.closeRecentPlays)
      ? item.closeRecentPlays
      : [])
      .map((track) => normalizeRecentMovementTrack(track))
      .filter(Boolean)
      .slice(0, 2),
    recentPlayed: (Array.isArray(item?.recentPlayed) ? item.recentPlayed : [])
      .map((track) => normalizeRecentMovementTrack(track))
      .filter(Boolean)
      .slice(0, 2),
    lastMovement,
  };
};

const isRecentMovementFresh = ({
  value,
  nowMs = Date.now(),
  windowMinutes = 120,
} = {}) => {
  const timestamp = new Date(value || '').getTime();
  const safeNowMs = Number(nowMs);
  const safeWindowMinutes = Number(windowMinutes);
  if (
    !Number.isFinite(timestamp) ||
    !Number.isFinite(safeNowMs) ||
    !Number.isFinite(safeWindowMinutes) ||
    safeWindowMinutes <= 0
  ) {
    return false;
  }
  if (timestamp > safeNowMs) return false;
  return safeNowMs - timestamp <= safeWindowMinutes * 60 * 1000;
};

export const getRadioRecentMovementDisplay = (
  recentMovement = null,
  { surface = 'expanded_dock', nowMs = Date.now() } = {},
) => {
  const safeMovement = normalizeRecentMovement(recentMovement);
  const closeRecentPlays = safeMovement.closeRecentPlays;
  if (closeRecentPlays.length) {
    return {
      kind: 'close',
      label: 'Recent Movement',
      headline: `${closeRecentPlays.length} ${
        closeRecentPlays.length === 1 ? 'record' : 'records'
      } played in the last ${safeMovement.activeWindowMinutes} minutes`,
      records: closeRecentPlays,
    };
  }

  if (surface === 'small_capsule') return null;

  if (safeMovement.recentPlayed.length) {
    return {
      kind: 'recent',
      label: 'Recent Movement',
      headline: 'Recently played inside JG Radio',
      records: safeMovement.recentPlayed,
    };
  }

  if (
    safeMovement.lastMovement &&
    isRecentMovementFresh({
      value: safeMovement.lastMovement.at,
      nowMs,
      windowMinutes: safeMovement.recentWindowMinutes,
    })
  ) {
    return {
      kind: 'last',
      label: 'Last Movement',
      headline: 'Last movement inside JG Radio',
      records: [
        {
          trackId: safeMovement.lastMovement.trackId,
          title: safeMovement.lastMovement.title,
          artistName: safeMovement.lastMovement.artistName,
          coverArtUrl: '',
          lastPlayedAt: safeMovement.lastMovement.at,
          playCount: 0,
        },
      ],
    };
  }

  return {
    kind: 'quiet',
    label: 'Rotation Ready',
    headline: 'Records are ready in rotation. Press play and move the room.',
    records: [],
  };
};

const isLiveCapsuleTrack = (item) =>
  item?.kind === RADIO_QUEUE_KINDS.TRACK &&
  Boolean(getTrackTitle(item)) &&
  Boolean(getTrackArtistName(item)) &&
  Boolean(cleanText(item?.audioUrl, 2048));

export const getNextTrackForLiveCapsule = ({
  queue = [],
  currentIndex = 0,
} = {}) => {
  const safeQueue = Array.isArray(queue) ? queue : [];
  const queueLength = safeQueue.length;
  if (queueLength <= 1) return null;

  const safeCurrentIndex = normalizeIndex(currentIndex, queueLength);
  for (let offset = 1; offset < queueLength; offset += 1) {
    const candidateIndex = (safeCurrentIndex + offset) % queueLength;
    const candidate = safeQueue[candidateIndex];
    if (isLiveCapsuleTrack(candidate)) {
      return {
        trackId: cleanText(candidate.trackId || candidate.id, 120),
        title: getTrackTitle(candidate),
        artistName: getTrackArtistName(candidate),
      };
    }
  }

  return null;
};

export const normalizeRadioLiveCapsuleSignals = (response = null) => {
  const data = response?.data?.data || response?.data || response || {};
  const rotationDepth = Number(data.rotationDepth || 0);
  const hasRecentMovementPayload =
    data.recentMovement && typeof data.recentMovement === 'object';
  const newInRotation = (Array.isArray(data.newInRotation)
    ? data.newInRotation
    : [])
    .map((item) => normalizeNewRotationSignalTrack(item))
    .filter(Boolean)
    .slice(0, 3);
  const feedbackSignals = (Array.isArray(data.feedbackSignals)
    ? data.feedbackSignals
    : [])
    .map((item) => normalizeSignalTrack(item, 'receivedAt'))
    .filter(Boolean)
    .slice(0, 3);

  return {
    generatedAt: toIsoString(data.generatedAt),
    rotationDepth:
      Number.isFinite(rotationDepth) && rotationDepth > 0
        ? Math.floor(rotationDepth)
        : 0,
    newInRotation,
    feedbackSignals,
    recentMovement: hasRecentMovementPayload
      ? normalizeRecentMovement(data.recentMovement)
      : null,
    programmingMessage: normalizeProgrammingMessage(data.programmingMessage),
    proofSummary: normalizeProofSummary(data.proofSummary),
  };
};

export const buildRadioLiveCapsuleMessage = ({
  currentCapsule = null,
  queue = [],
  currentIndex = 0,
  isPlaying = false,
  signals = null,
  transientFeedbackSignal = null,
  transientNewInRotationSignal = null,
  transientProofSummary = null,
  nowMs = Date.now(),
  feedbackSignalTtlMs = 4000,
} = {}) => {
  if (!currentCapsule) {
    return {
      brand: 'Joe Galaxy Radio',
      status: 'Radio Rotation',
      title: RADIO_LIVE_CAPSULE_READY_TEXT,
      artistName: '',
      liveLabel: '',
      isReady: false,
      isPlaying: false,
      secondaryLabel: '',
      secondaryTitle: '',
      secondaryArtistName: '',
      upNext: null,
      recentMovementSummary: null,
      proofSummary: {
        hasProofActivity: false,
        primaryDisplay: '',
      },
      proofSignal: null,
    };
  }

  const isStationProgramming =
    currentCapsule.kind === RADIO_QUEUE_KINDS.BUMPER ||
    currentCapsule.kind === RADIO_QUEUE_KINDS.DROP;
  const upNext = getNextTrackForLiveCapsule({ queue, currentIndex });
  const safeSignals = normalizeRadioLiveCapsuleSignals(signals);
  const programmingMessage = safeSignals.programmingMessage || null;
  const proofSummary = safeSignals.proofSummary || {
    hasProofActivity: false,
    primaryDisplay: '',
  };
  const transientSignal = normalizeSignalTrack(
    transientFeedbackSignal,
    'receivedAt',
  );
  const feedbackSignal =
    transientSignal ||
    safeSignals.feedbackSignals.find((signal) =>
      isFreshSignal(signal, 'receivedAt', nowMs, feedbackSignalTtlMs),
    ) ||
    null;
  const normalizedNewInRotationSignal = safeSignals.newInRotation[0] || null;
  const newInRotationSignal = normalizeNewRotationSignalTrack(
    transientNewInRotationSignal,
  );
  const proofSignalSummary = normalizeProofSummary(transientProofSummary);
  const proofSignal =
    proofSignalSummary.hasProofActivity && proofSignalSummary.primaryDisplay
      ? proofSignalSummary
      : null;

  let secondaryLabel = 'In Rotation';
  let secondaryTitle = '';
  let secondaryArtistName = '';
  if (programmingMessage) {
    secondaryLabel = programmingMessage.eyebrow || 'Radio Message';
    secondaryTitle = programmingMessage.message;
  } else if (feedbackSignal) {
    secondaryLabel = 'Feedback Landed';
    secondaryTitle = feedbackSignal.title;
    secondaryArtistName = feedbackSignal.artistName;
  } else if (newInRotationSignal) {
    secondaryLabel = 'NEW IN ROTATION';
    secondaryTitle = newInRotationSignal.title;
    secondaryArtistName = newInRotationSignal.artistName;
  } else if (proofSignal) {
    secondaryLabel = 'REAL HITLAB SIGNAL';
    secondaryTitle = proofSignal.primaryDisplay;
  } else if (upNext) {
    secondaryLabel = 'Up Next';
    secondaryTitle = upNext.title;
    secondaryArtistName = upNext.artistName;
  } else if (safeSignals.rotationDepth > 0) {
    secondaryLabel = `${safeSignals.rotationDepth} ${
      safeSignals.rotationDepth === 1 ? 'Record' : 'Records'
    } In Rotation`;
  }

  return {
    brand: 'Joe Galaxy Radio',
    status: isStationProgramming ? 'In Rotation' : isPlaying ? 'Now Playing' : 'Ready to play',
    title: currentCapsule.title || 'Radio Rotation',
    artistName: isStationProgramming ? 'Radio Rotation' : currentCapsule.artistName || '',
    liveLabel: 'Live',
    isReady: Boolean(currentCapsule.canPlay),
    isPlaying: Boolean(isPlaying),
    secondaryLabel,
    secondaryTitle,
    secondaryArtistName,
    hasDisplayedPriority: Boolean(programmingMessage || feedbackSignal || newInRotationSignal || proofSignal),
    upNext,
    recentMovementSummary: safeSignals.recentMovement
      ? getRadioRecentMovementDisplay(safeSignals.recentMovement, {
          surface: 'small_capsule',
          nowMs,
        })
      : null,
    programmingMessage,
    feedbackSignal,
    newInRotationSignal: newInRotationSignal || normalizedNewInRotationSignal,
    proofSummary,
    proofSignal,
    rotationDepth: safeSignals.rotationDepth,
  };
};
